import { FC, useEffect, useRef, useState } from 'react';
import { Button, Form, Image, Modal } from 'antd';
import { useMethods } from '@lhb/hook';
import V2Table from '@/common/components/Data/V2Table';
import V2SearchForm from '@/common/components/Form/V2SearchForm';
import V2FormProvinceList from '@/common/components/Form/V2FormProvinceList';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import { getPCDInfo } from '@/common/utils/map';
import { getQiniuFilePreviewUrl } from '@/common/utils/qiniu';
import { createPlace } from '@/common/api/common';
import V2Form from '@/common/components/Form/V2Form';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import NormalAddress from '@/common/components/business/DynamicComponent/components/Address/NormalAddress';
import { resourceCategoryList, resourceProjectPage, resourceSpotDetail, resourceSpotPage } from '@/common/api/expandStore/chancePointSlowFlash';
import { normalizeProjectCategoryOptions, normalizeSpotCategoryOptions, ProjectSpotValue, selectProject, selectSpot, validateProjectSpot } from './value';
import { backfillSpotProperties, clearSpotProperties } from './backfill';
import styles from './index.module.less';

/** 62 为一个动态属性，两个选择字段始终以完整对象同步至 textValue。 */
const ProjectSpotSelect: FC<{
  propertyItem: any;
  form: any;
  disabled?: boolean;
  required?: boolean;
  taskId?: number;
  projectSearchDefaults?: { city?: (number | null | undefined)[]; projectName?: string | null; longitude?: number | string | null; latitude?: number | string | null };
  tiledFormDataRef?: { current: any[] };
  updateRelateShow?: (component: any) => void;
  updateCompValue?: (identification: string, value: ProjectSpotValue) => void;
}> = ({ propertyItem, form, disabled, required = false, taskId, projectSearchDefaults, tiledFormDataRef, updateRelateShow, updateCompValue }) => {
  const identification = propertyItem.identification;
  const watched = Form.useWatch(identification, form);
  // useWatch 仅包含已注册子字段；ID 等元数据必须从完整表单值读取。
  const value: ProjectSpotValue = form.getFieldValue(identification) || watched || {};
  const [picker, setPicker] = useState<'project' | 'spot'>();
  const [filters, setFilters] = useState<{ keyword: string; cityId?: number }>({ keyword: '' });
  const [pendingProject, setPendingProject] = useState<ProjectSpotValue>();
  const [pendingSpot, setPendingSpot] = useState<ProjectSpotValue>();
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [createAddressItem, setCreateAddressItem] = useState({ identification: 'address', name: '详细地址', textValue: '' });
  const creatingRef = useRef(false);
  const addressRevision = useRef(0);
  const createRegion = useRef<{ provinceId: number; cityId: number; districtId: number }>();
  const [createRegionLoading, setCreateRegionLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [spotCreateOpen, setSpotCreateOpen] = useState(false);
  const [spotCreateForm] = Form.useForm();
  const [projectCategoryOptions, setProjectCategoryOptions] = useState<any[]>([]);
  const [spotCategoryOptions, setSpotCategoryOptions] = useState<any[]>([]);
  const [resourceCategoryLoading, setResourceCategoryLoading] = useState(false);

  const [error, setError] = useState('');
  const requestId = useRef(0);
  const spotDetailRequestId = useRef(0);
  const [loadingSpotId, setLoadingSpotId] = useState<number>();
  const selectionRevision = useRef(0);

  useEffect(() => () => { requestId.current += 1; spotDetailRequestId.current += 1; addressRevision.current += 1; }, []);

  const methods = useMethods({
    initializeProject() {
      if (!taskId || disabled || form.getFieldValue(identification)?.projectId) return;
      let active = true;
      const revision = selectionRevision.current;
      resourceProjectPage({ taskId, page: 1, size: 1 }).then((res) => {
        const project = res?.defaultProject;
        if (!active || selectionRevision.current !== revision || form.getFieldValue(identification)?.projectId || !project?.projectId) return;
        const next = selectProject({}, project);
        form.setFieldValue(identification, next);
        updateCompValue?.(identification, next);
      }, () => {
        // 默认项目加载失败仍允许用户手动查询和选择，请求层统一提示错误。
      }).finally(() => {});
      return () => { active = false; };
    },
    openProject() {
      if (disabled) return;
      methods.cancelSpotDetail();
      const current = form.getFieldValue(identification);
      setPendingProject(current?.projectId ? current : undefined);
      setFilters({ keyword: '' });
      setError('');
      setPicker('project');
    },
    openSpot() {
      if (disabled || !value.projectId) return;
      methods.cancelSpotDetail();
      const current = form.getFieldValue(identification);
      setPendingSpot(current?.spotId ? current : undefined);
      setFilters({ keyword: '' });
      setError('');
      setPicker('spot');
    },
    closePicker() {
      requestId.current += 1;
      methods.cancelSpotDetail();
      setPicker(undefined);
    },
    cancelSpotDetail() {
      spotDetailRequestId.current += 1;
      setLoadingSpotId(undefined);
    },
    openCreateProject() {
      if (disabled) return;
      addressRevision.current += 1;
      createRegion.current = undefined;
      setCreateRegionLoading(false);
      createForm.resetFields();
      createForm.setFieldsValue({ name: filters.keyword });
      setCreateAddressItem({ identification: 'address', name: '详细地址', textValue: '' });
      setCreateError('');
      methods.loadResourceCategories();
      setCreateOpen(true);
      const { longitude, latitude } = projectSearchDefaults || {};
      if ([longitude, latitude].every(coord => coord !== null && coord !== undefined && `${coord}`.trim() !== '' && Number.isFinite(Number(coord)))) {
        methods.changeCreateAddress({ lng: Number(longitude), lat: Number(latitude) }, true);
      }
    },
    closeCreateProject() {
      if (!creatingRef.current) {
        addressRevision.current += 1;
        setCreateOpen(false);
      }
    },
    changeCreateAddress(params: any, initialize = false) {
      const revision = ++addressRevision.current;
      createRegion.current = undefined;
      setCreateError('');
      setCreateRegionLoading(false);
      createForm.setFieldValue('address', params.name || '');
      createForm.setFields([{ name: 'address', errors: [] }]);
      if ((!initialize && !params.name) || params.lng === undefined || params.lat === undefined || params.lng === null || params.lat === null) return;
      setCreateRegionLoading(true);
      return getPCDInfo([params.lng, params.lat]).then((region: any) => {
        if (revision !== addressRevision.current) return;
        const { provinceId, cityId, districtId } = region || {};
        if (!provinceId || !cityId || !districtId) {
          setCreateError('未能识别地址的省市区，请重新选择详细地址');
          return;
        }
        if (initialize) {
          if (!region.address) {
            setCreateError('未能识别商圈的详细地址，请重新选择详细地址');
            return;
          }
          createForm.setFieldValue('address', region.address);
          setCreateAddressItem({
            identification: 'address',
            name: '详细地址',
            textValue: JSON.stringify({ address: region.address, longitude: params.lng, latitude: params.lat }),
          });
        }
        createRegion.current = { provinceId, cityId, districtId };
      }, () => {
        if (revision === addressRevision.current) setCreateError('地址省市区查询失败，请重新选择详细地址');
      }).finally(() => {
        if (revision === addressRevision.current) setCreateRegionLoading(false);
      });
    },
    openCreateSpot() {
      if (disabled || !value.projectId) return;
      methods.cancelSpotDetail();
      spotCreateForm.resetFields();
      spotCreateForm.setFieldsValue({ spotName: filters.keyword });
      methods.loadResourceCategories();
      setSpotCreateOpen(true);
    },
    closeCreateSpot() { setSpotCreateOpen(false); },
    submitCreateSpot() {
      spotCreateForm.validateFields().then((spotValues) => {
        methods.change({ projectId: value.projectId, projectName: value.projectName, spotName: spotValues.spotName, spotCategoryId: spotValues.spotCategoryId, spotId: null, spotPublicId: null, spotTenantId: null });
        setSpotCreateOpen(false);
      }, () => {
        // 保留表单校验错误。
      });
    },
    loadResourceCategories() {
      if ((projectCategoryOptions.length && spotCategoryOptions.length) || resourceCategoryLoading) return Promise.resolve();
      setResourceCategoryLoading(true);
      return Promise.all([
        resourceCategoryList({ resourcesType: 0 }),
        resourceCategoryList({ resourcesType: 1 }),
      ]).then(([projectRes, spotRes]) => {
        setProjectCategoryOptions(normalizeProjectCategoryOptions(projectRes));
        setSpotCategoryOptions(normalizeSpotCategoryOptions(spotRes));
      }, () => {
        setError('资源类目加载失败，请重试');
      }).finally(() => {
        setResourceCategoryLoading(false);
      });
    },
    submitCreateProject() {
      if (creatingRef.current || disabled || createRegionLoading) return;
      creatingRef.current = true;
      setCreateError('');
      createForm.validateFields().then((values) => {
        if (!createRegion.current) {
          setCreateError('请重新选择详细地址，获取省市区信息后再创建');
          return;
        }
        setCreateLoading(true);
        return createPlace({
          name: values.name,
          address: values.address,
          categoryId: values.categoryId,
          ...createRegion.current,
        }).then((res) => {
          const projectId = typeof res === 'number' ? res : res?.id;
          const project = projectId ? { projectId, projectName: res?.name || values.name, address: res?.address || values.address } : null;
          if (!project) {
            setCreateError('未取得新项目 ID，请先关闭弹窗查询项目，避免重复创建');
            return;
          }
          methods.change(project);
          setCreateOpen(false);
          V2Message.success('创建项目成功');
        }, () => {
          setCreateError('创建项目失败，请重试');
        }).finally(() => {});
      }, () => {
        // 必填校验失败时保留表单及字段错误。
      }).finally(() => {
        creatingRef.current = false;
        setCreateLoading(false);
      });
    },
    normalizeProjectSearch(values) {
      const cityPath = values?.city;
      const city = Array.isArray(cityPath) && cityPath.length === 2 && cityPath.every(id => typeof id === 'number' && Number.isFinite(id) && id > 0) ? cityPath : undefined;
      const projectName = typeof values?.projectName === 'string' ? values.projectName.trim() : '';
      return { city, projectName: projectName || undefined };
    },
    searchProjects(values) {
      const cityPath = values.city;
      setFilters({ keyword: values.projectName || '', cityId: Array.isArray(cityPath) && cityPath.length === 2 ? cityPath[1] : undefined });
    },
    selectProjectRow(record: ProjectSpotValue) {
      setPendingProject(record);
    },
    selectSpotRow(record: ProjectSpotValue) {
      methods.cancelSpotDetail();
      setError('');
      setPendingSpot(record);
    },
    confirmSelection() {
      if (disabled || loadingSpotId !== undefined) return;
      if (picker === 'project' && pendingProject?.projectId) methods.chooseProject(pendingProject);
      if (picker === 'spot' && pendingSpot?.spotId) methods.chooseSpot(pendingSpot);
    },
    search(keyword: string) { setFilters({ ...filters, keyword }); },
    load(params: any = {}) {
      const currentRequest = ++requestId.current;
      setError('');
      const paging = { page: params.page || 1, size: params.size || 20 };
      const request = picker === 'project'
        ? resourceProjectPage({ ...paging, ...(taskId ? { taskId } : {}), ...(filters.keyword ? { projectName: filters.keyword } : {}), ...(filters.cityId ? { cityId: filters.cityId } : {}) })
        : resourceSpotPage({ ...paging, projectId: value.projectId, spotName: filters.keyword });
      return request.then((res) => {
        if (currentRequest !== requestId.current) return { dataSource: [], count: 0 };
        return picker === 'project'
          ? { dataSource: res?.objectList || [], count: res?.totalNum || 0 }
          : { dataSource: res?.data || [], count: res?.meta?.total || 0 };
      }, () => {
        if (currentRequest === requestId.current) setError('加载失败，请重新搜索或重新打开');
        return { dataSource: [], count: 0 };
      }).finally(() => {});
    },
    chooseProject(record: ProjectSpotValue) {
      if (disabled) return;
      methods.change(selectProject(form.getFieldValue(identification) || {}, record));
    },
    chooseSpot(record: ProjectSpotValue) {
      if (disabled) return;
      const next = selectSpot(form.getFieldValue(identification) || {}, record);
      const message = validateProjectSpot(next, true);
      if (message) { setError(message); return; }
      if (!record.spotId || !record.spotTenantId) { setError('点位信息不完整，请重新选择点位'); return; }
      const currentRequest = ++spotDetailRequestId.current;
      setLoadingSpotId(record.spotId);
      setError('');
      resourceSpotDetail({ spotId: record.spotId, spotTenantId: record.spotTenantId }).then((res) => {
        if (currentRequest !== spotDetailRequestId.current) return;
        const detail = res?.data || res;
        const { importedComponents, formValues } = backfillSpotProperties(detail?.propertyValueList, tiledFormDataRef?.current || []);
        // 逐字段替换，避免 setFieldsValue 深合并后残留上一个点位的商务条件子字段。
        Object.entries(formValues).forEach(([key, fieldValue]) => form.setFieldValue(key, fieldValue));
        importedComponents.forEach(component => updateRelateShow?.(component));
        methods.change(next, true);
      }, () => {
        if (currentRequest === spotDetailRequestId.current) setError('点位详情加载失败，请重试');
      }).finally(() => {
        if (currentRequest === spotDetailRequestId.current) setLoadingSpotId(undefined);
      });
    },
    change(next: ProjectSpotValue, keepSpotProperties = false) {
      const current = form.getFieldValue(identification) || {};
      const currentSpotKey = [current.spotId, current.spotPublicId, current.spotTenantId, current.spotName].join('|');
      const nextSpotKey = [next.spotId, next.spotPublicId, next.spotTenantId, next.spotName].join('|');
      if (!keepSpotProperties && currentSpotKey !== nextSpotKey) {
        const { importedComponents, formValues } = clearSpotProperties(tiledFormDataRef?.current || []);
        Object.entries(formValues).forEach(([key, fieldValue]) => form.setFieldValue(key, fieldValue));
        importedComponents.forEach(component => updateRelateShow?.(component));
      }
      selectionRevision.current += 1;
      form.setFieldValue(identification, next);
      updateCompValue?.(identification, next);
      form.setFields([{ name: [identification, 'projectName'], errors: [] }, { name: [identification, 'spotName'], errors: [] }]);
      methods.closePicker();
    },
    clearProject(event) {
      event.stopPropagation();
      if (!disabled && event.target.value === '') methods.change({});
    },
    clearSpot(event) {
      event.stopPropagation();
      if (disabled || event.target.value !== '') return;
      const current = form.getFieldValue(identification) || {};
      methods.change({ projectId: current.projectId, projectName: current.projectName });
    },
    preventTyping(event) { event.preventDefault(); },
    preventEditingKey(event) {
      if (event.key === 'Backspace' || event.key === 'Delete') event.preventDefault();
    },
    validateProject() {
      const current = form.getFieldValue(identification) || {};
      if (disabled || !required || current.projectId) return Promise.resolve();
      return Promise.reject(new Error('请选择项目'));
    },
    validateSpot() {
      const message = disabled ? undefined : validateProjectSpot(form.getFieldValue(identification), required);
      return message ? Promise.reject(new Error(message)) : Promise.resolve();
    },
    renderProjectImage(pictures) {
      return pictures?.[0]?.url ? <Image width={35} height={35} src={getQiniuFilePreviewUrl(pictures[0].url)} preview={{ src: pictures[0].url }} /> : '-';
    },
    renderVolume(volume) { return volume === null || volume === undefined ? '-' : `${volume / 10000}万m²`; },
    renderSpotImage(pictures) {
      const url = pictures?.[0]?.url;
      return url ? <Image width={35} height={35} src={getQiniuFilePreviewUrl(url)} preview={{ src: url }} /> : '-';
    },
    renderSpotValue(value) {
      const displayValue = value && typeof value === 'object' ? value.name : value;
      return displayValue === null || displayValue === undefined || displayValue === '' ? '-' : `${displayValue}`;
    },
    renderSpotArea(area) {
      return area === null || area === undefined || area === '' ? '-' : `${area}㎡`;
    },
  });

  useEffect(() => methods.initializeProject(), [taskId, identification, disabled]);

  const projectColumns = [
    { title: '项目名称', key: 'projectName', width: 150, ellipsis: true },
    { title: '项目图片', key: 'placePicture', noControlled: true, render: methods.renderProjectImage, width: 81 },
    { title: '商业体量', key: 'commercialVolume', render: methods.renderVolume, width: 93 },
    { title: '项目地址', key: 'address', width: 164, ellipsis: true },
  ];
  const spotColumns = [
    { title: '点位名称', key: 'spotName', render: methods.renderSpotValue, width: 220 },
    {
      title: '点位图片',
      key: 'picture',
      noControlled: true,
      render: methods.renderSpotImage,
      width: 90,
    },
    {
      title: '楼层',
      key: 'floor',
      noControlled: true,
      render: methods.renderSpotValue,
      width: 70,
    },
    {
      title: '位置类型',
      key: 'spotPosition',
      noControlled: true,
      render: methods.renderSpotValue,
      width: 110,
    },
    {
      title: '面积',
      key: 'area',
      noControlled: true,
      render: methods.renderSpotArea,
      width: 100,
    },
  ];

  return <>
    <div className={styles.projectSpot}>
      <div className={styles.field}>
        <V2FormInput
          label='项目名称'
          name={[identification, 'projectName']}
          className={styles.selector}
          allowClear
          placeholder='请选择项目'
          disabled={disabled}
          rules={[{ validator: methods.validateProject }]}
          onChange={methods.clearProject}
          onClickInput={methods.openProject}
          config={{ onBeforeInput: methods.preventTyping, onPaste: methods.preventTyping, onDrop: methods.preventTyping, onKeyDown: methods.preventEditingKey }}
        />
      </div>
      <div className={styles.field}>
        <V2FormInput
          label='点位名称'
          name={[identification, 'spotName']}
          className={styles.selector}
          allowClear
          placeholder='请选择点位'
          disabled={disabled || !value.projectId}
          rules={[{ validator: methods.validateSpot }]}
          onChange={methods.clearSpot}
          onClickInput={methods.openSpot}
          config={{ onBeforeInput: methods.preventTyping, onPaste: methods.preventTyping, onDrop: methods.preventTyping, onKeyDown: methods.preventEditingKey }}
        />
      </div>
    </div>
    {picker ? <Modal
      open
      title={picker === 'project' ? '选择项目' : '选择点位'}
      width={picker === 'project' ? 850 : 720}
      className={styles.projectPicker}
      centered
      okText='确定'
      cancelText='取消'
      okButtonProps={{ disabled: disabled || !(picker === 'project' ? pendingProject?.projectId : pendingSpot?.spotId) }}
      confirmLoading={loadingSpotId !== undefined}
      onOk={methods.confirmSelection}
      onCancel={methods.closePicker}
      destroyOnClose
    >
      <div className={styles.picker}>
        {picker === 'project' ? <V2SearchForm
          onSearch={methods.searchProjects}
          showSearchNum={2}
          moreBtn={false}
          rightOperate={<Button type='primary' onClick={methods.openCreateProject} disabled={disabled}>创建项目</Button>}
        >
          <V2FormProvinceList label='城市' name='city' type={2} multiple={false} />
          <V2FormInput label='项目名称' name='projectName' placeholder='请输入项目名称' />
        </V2SearchForm> : <V2SearchForm onSearch={(values) => methods.search(values.spotName || '')} showSearchNum={1} moreBtn={false} rightOperate={<Button type='primary' onClick={methods.openCreateSpot} disabled={disabled}>新增点位</Button>}>
          <V2FormInput label='点位名称' name='spotName' placeholder='请输入点位名称' />
        </V2SearchForm>}
        {error ? <div className={styles.error} role='alert'>{error}</div> : null}
        <V2Table
          key={`${picker}-${filters.keyword}-${filters.cityId || ''}`}
          rowKey={picker === 'project' ? 'projectId' : 'spotId'}
          defaultColumns={picker === 'project' ? projectColumns : spotColumns}
          onFetch={methods.load}
          hideColumnPlaceholder
          floorWidth={31}
          tableLayout='fixed'
          emptyRender={picker === 'spot' ? <span>暂无点位，<Button type='link' onClick={methods.openCreateSpot} disabled={disabled}>新增点位</Button></span> : true}
          scroll={{ x: undefined, y: picker === 'project' ? 258 : 360 }}
          rowSelection={{
            type: 'radio',
            columnWidth: 31,
            selectedRowKeys: picker === 'project'
              ? (pendingProject?.projectId ? [pendingProject.projectId] : [])
              : (pendingSpot?.spotId ? [pendingSpot.spotId] : []),
            preserveSelectedRowKeys: true,
            onSelect: picker === 'project' ? methods.selectProjectRow : methods.selectSpotRow,
            getCheckboxProps: record => ({ disabled, 'aria-label': `选择${picker === 'project' ? record.projectName : record.spotName}` }),
          }}
          paginationConfig={picker === 'project' ? { className: styles.projectPagination, simple: true, size: 'small', showSizeChanger: false, showQuickJumper: false, showTotal: undefined } : undefined}
        />
      </div>
    </Modal> : null}
    <Modal open={spotCreateOpen} title='新增点位' width={420} okText='确定' cancelText='取消' onOk={methods.submitCreateSpot} onCancel={methods.closeCreateSpot} destroyOnClose>
      <V2Form form={spotCreateForm} layout='vertical'>
        <V2FormInput label='点位名称' name='spotName' required rules={[{ required: true, whitespace: true, message: '请输入点位名称' }]} placeholder='请输入点位名称' />
        <V2FormSelect
          label='点位类目'
          name='spotCategoryId'
          required
          options={spotCategoryOptions}
          disabled={resourceCategoryLoading}
          placeholder={resourceCategoryLoading ? '正在加载点位类目' : '请选择点位类目'}
          config={{ fieldNames: { label: 'name', value: 'id' }, showSearch: true }}
        />
      </V2Form>
    </Modal>
    <Modal
      open={createOpen}
      title='创建项目'
      width={520}
      okText='确定'
      cancelText='取消'
      confirmLoading={createLoading}
      okButtonProps={{ disabled: createRegionLoading }}
      closable={!createLoading}
      keyboard={!createLoading}
      cancelButtonProps={{ disabled: createLoading }}
      maskClosable={false}
      onOk={methods.submitCreateProject}
      onCancel={methods.closeCreateProject}
      destroyOnClose
    >
      {createError ? <div role='alert'>{createError}</div> : null}
      <V2Form form={createForm} layout='vertical' disabled={createLoading}>
        <V2FormSelect
          label='项目类型'
          name='categoryId'
          required
          options={projectCategoryOptions}
          disabled={resourceCategoryLoading}
          placeholder={resourceCategoryLoading ? '正在加载项目类型' : '请选择项目类型'}
          config={{ fieldNames: { label: 'name', value: 'id' }, showSearch: true }}
        />
        <V2FormInput label='项目名称' name='name' required rules={[{ required: true, whitespace: true, message: '请输入项目名称' }]} placeholder='请输入项目名称' />
        {createOpen ? <NormalAddress
          key={createAddressItem.textValue || 'empty'}
          form={createForm}
          formItemData={createAddressItem}
          disabled={createLoading}
          required
          addressChange={methods.changeCreateAddress}
          mapZoomChange={undefined}
          mapMoveChange={undefined}
          mapConfig={{}}
        /> : null}
      </V2Form>
    </Modal>
  </>;
};

export default ProjectSpotSelect;
